import { tv } from 'tailwind-variants';
import { type VideoStyles, videoStyles } from '@/app_components/Video/Video';
import { cn } from '@/app_lib/utils/utils';

const videoLoaderStyles = tv({
  defaultVariants: {
    layout: 'default',
    orientation: 'vertical',
  },
  slots: {
    base: '**:cursor-default select-none',
    content: 'gap-1 h-fit',
    date: 'animate-pulse bg-helper text-transparent! w-fit leading-none',
    description: 'animate-pulse bg-helper text-transparent! leading-none',
    player: 'animate-pulse bg-helper',
    thumbnail: 'animate-pulse bg-helper',
    title: 'w-full',
  },
  variants: {
    layout: {
      default: {},
      hero: {},
    },
    modal: {
      false: {},
      true: {},
    },
    orientation: {
      horizontal: {},
      vertical: {},
    },
  },
});

export const VideoBase = ({
  children,
  layout,
  orientation,
  modal = false,
  className,
}: { children: React.ReactNode; modal?: boolean; className?: string } & VideoStyles) => (
  <div
    aria-hidden='true'
    className={cn(
      'group/card',
      videoStyles({ layout, modal, orientation }).base(),
      videoLoaderStyles({ layout, modal, orientation }).base(),
      className,
    )}
    role='presentation'
  >
    {children}
  </div>
);

export const VideoContent = ({ children, layout, orientation }: { children: React.ReactNode } & VideoStyles) => (
  <div
    className={cn(videoStyles({ layout, orientation }).content(), videoLoaderStyles({ layout, orientation }).content())}
  >
    {children}
  </div>
);

// Static Image version
export const VideoThumbnail = ({
  layout,
  orientation,
  modal = false,
  className,
  'data-index': index,
  ref,
}: {
  watching?: boolean;
  modal?: boolean;
  duration?: string;
  className?: string;
  'data-index'?: number;
  ref?: React.RefObject<HTMLSpanElement>;
} & VideoStyles) => {
  return (
    <span
      className={cn(
        'group relative',
        videoStyles({ layout, modal, orientation }).thumbnail(),
        videoLoaderStyles({ layout, modal, orientation }).thumbnail(),
        className,
      )}
      data-index={index}
      ref={ref}
    />
  );
};

// Video Player version - using STN Video Player
export const VideoPlayer = ({ layout, orientation, modal = false }: { modal?: boolean } & VideoStyles) => (
  <span
    className={cn(
      videoStyles({ layout, modal, orientation }).player(),
      videoLoaderStyles({ layout, modal, orientation }).player(),
    )}
  />
);

export const VideoTitle = ({
  layout,
  orientation,
  modal = false,
  className,
  'data-index': index,
}: {
  modal?: boolean;
  className?: string;
  playing?: boolean;
  watching?: boolean;
  'data-index'?: number;
} & VideoStyles) => (
  <span
    aria-hidden='true'
    className={cn(
      videoStyles({ layout, modal, orientation }).title(),
      videoLoaderStyles({ layout, modal, orientation }).title(),
      className,
      'flex! flex-col gap-1',
    )}
    data-index={index}
  >
    <span className='animate-pulse bg-helper text-transparent! line-clamp-1 w-full'>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit.
    </span>
    <span className='animate-pulse bg-helper text-transparent! line-clamp-1 w-[72%]'>
      Sed do eiusmod tempor incididunt ut labore et dolore
    </span>
  </span>
);

export const VideoDescription = ({ layout, orientation, modal = false }: { modal?: boolean } & VideoStyles) => (
  <span
    aria-hidden='true'
    className={cn(
      videoStyles({ layout, modal, orientation }).description(),
      videoLoaderStyles({ layout, modal, orientation }).description(),
    )}
  >
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna
    aliqua. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et
    dolore magna aliqua.
  </span>
);

export const VideoDate = ({ layout, orientation, modal = false }: { modal?: boolean } & VideoStyles) => (
  <span
    aria-hidden='true'
    className={cn(
      videoStyles({ layout, modal, orientation }).date(),
      videoLoaderStyles({ layout, modal, orientation }).date(),
    )}
  >
    January 1, 2026
  </span>
);
